import { useIntl } from 'react-intl';
import { IconCustomAurora, IconMessage2, IconX } from '@rossum/ui/icons/tabler';
import {
  Badge,
  IconButton,
  Stack,
  SvgIcon,
  Switch,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { useIsFeatureEnabled } from '../../feature-enablement/featureEnablement';
import { ChatHistoryDropdown } from './ChatHistoryDropdown';
import { FilesDropdown } from './FilesDropdown';

type ChatHeaderProps = {
  mcpReadWrite: boolean;
  onMcpModeChange?: (enabled: boolean) => void;
  canChangeMcpMode: boolean;
  onCreateNew: () => void;
  onClose: () => void;
  onSelectChat: (chatId: string) => void;
  currentChatId: string | null;
  activeChatIds: Set<string>;
  unseenChatIds: Set<string>;
  outputFiles: Array<{ filename: string; url: string }>;
};

export const ChatHeader = ({
  mcpReadWrite,
  onMcpModeChange,
  canChangeMcpMode,
  onCreateNew,
  onClose,
  onSelectChat,
  currentChatId,
  activeChatIds,
  unseenChatIds,
  outputFiles,
}: ChatHeaderProps) => {
  const intl = useIntl();
  const isMrFabry = !useIsFeatureEnabled('mr-fabry-legacy');
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        px: 2,
        py: 1.5,
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: mcpReadWrite ? 'warning.main' : 'divider',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <SvgIcon fontSize="large" sx={{ color: 'aurora.main' }}>
          <IconCustomAurora />
        </SvgIcon>
        <Typography variant="h6" fontWeight={600}>
          {intl.formatMessage({ id: 'features.chat.header.title' })}
        </Typography>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1}>
        {onMcpModeChange ? (
          <WriteModeToggle
            enabled={mcpReadWrite}
            onChange={onMcpModeChange}
            disabled={!canChangeMcpMode}
            isAutoMode={isMrFabry}
          />
        ) : null}

        <FilesDropdown chatId={currentChatId} outputFiles={outputFiles} />

        <ChatHistoryDropdown
          onCreateNew={onCreateNew}
          onSelectChat={onSelectChat}
          currentChatId={currentChatId}
          activeChatIds={activeChatIds}
          unseenChatIds={unseenChatIds}
          renderTrigger={onClick => (
            <Tooltip
              title={intl.formatMessage({
                id: 'features.chat.header.messages',
              })}
            >
              <IconButton data-cy="chat-history-button" onClick={onClick}>
                <Badge badgeContent={unseenChatIds.size} color="warning">
                  <SvgIcon>
                    <IconMessage2 />
                  </SvgIcon>
                </Badge>
              </IconButton>
            </Tooltip>
          )}
        />

        <Tooltip
          title={intl.formatMessage({ id: 'features.chat.header.close' })}
        >
          <IconButton data-cy="chat-close-button" onClick={onClose}>
            <SvgIcon>
              <IconX />
            </SvgIcon>
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
};

type WriteModeToggleProps = {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  disabled: boolean;
  // mr-fabry asks to approve each write instead of refusing it, so its toggle is "auto", not "write".
  isAutoMode: boolean;
};

const WriteModeToggle = ({
  enabled,
  onChange,
  disabled,
  isAutoMode,
}: WriteModeToggleProps) => {
  const intl = useIntl();
  return (
    <Tooltip
      title={intl.formatMessage({
        id: isAutoMode
          ? 'features.chat.header.autoMode.tooltip'
          : 'features.chat.header.writeMode.tooltip',
      })}
    >
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: enabled ? 'warning.main' : 'text.secondary',
            fontWeight: enabled ? 600 : 400,
            textTransform: 'uppercase',
            fontSize: 11,
          }}
        >
          {intl.formatMessage({
            id: isAutoMode
              ? 'features.chat.header.autoMode.label'
              : 'features.chat.header.writeMode.label',
          })}
        </Typography>
        <Switch
          data-cy="chat-write-mode-toggle"
          checked={enabled}
          onChange={e => onChange(e.target.checked)}
          disabled={disabled}
          color="warning"
          sx={{ ml: 'auto' }}
        />
      </Stack>
    </Tooltip>
  );
};
